import React, { useState, useRef } from "react";
import styled, { createGlobalStyle } from "styled-components";
import { Upload, Table, Input, Button, Space, message, Typography, Spin, Progress, Tooltip, Tag, Modal } from "antd";
import { InboxOutlined, DeleteOutlined, PlusOutlined, ArrowRightOutlined, FileOutlined } from "@ant-design/icons";
import PLPSTemplate from "./PLPSTemplate";
import { PLPSContext } from "./plpsContext";
import {
  uploadPLPSFile,
  digitizePLPSFile,
  getPLPSDigitizationPercent,
  fileToBase64,
} from "../../../services/plpsService";
import { useAuth } from "../../contexts/AuthContext";

const { Dragger } = Upload;
const { Text, Title } = Typography;

const GlobalStyle = createGlobalStyle`
  * {
    font-family: 'Open Sans', sans-serif !important;
  }
`;

const StyledButton = styled(Button)`
  background: #e3ecff;
  color: #426bba !important;
  font-weight: 600;
  font-size: 14px;
  border: none;
  border-radius: 1rem;
  box-shadow: 0 2px 6px rgba(66, 107, 186, 0.2);
  padding: 4px 14px;

  &:hover {
    background: #d7e4ff !important;
    color: #426bba !important;
  }
`;

export const StyledTableWrapper = styled.div`
  .ant-table-thead > tr > th {
    background: #426bba !important;
    border: 1px solid #426bba;
    color: #fefeff !important;
    font-weight: 600;
    font-size: 12px;
    line-height: 1.2;
  }

  .ant-table-tbody > tr > td {
    background: linear-gradient(
      150deg,
      rgba(235, 240, 255, 1) 25%,
      rgba(225, 235, 255, 1) 50%,
      rgba(215, 230, 255, 1) 100%
    );
    border: 1px solid #bfd3f2;
    color: #2c4c91;
    font-size: 11px;
    padding: 6px 12px;
    line-height: 1;
  }
`;

const FileNameTag = ({ fileName, showIconOnly = false }) => (
  <Tooltip title={fileName}>
    <Tag
      color="blue"
      style={{
        fontSize: 12,
        display: "flex",
        alignItems: "center",
        maxWidth: 200,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        flex: 1,
        cursor: "pointer",
      }}
    >
      <FileOutlined />
      {!showIconOnly && (
        <span
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: 1,
          }}
        >
          {fileName}
        </span>
      )}
    </Tag>
  </Tooltip>
);

const PLPS = ({ onBack }) => {
  const [fileList, setFileList] = useState([]);
  const [protocolId, setProtocolId] = useState(null);
  const [protocolName, setProtocolName] = useState(null);
  const [metadata, setMetadata] = useState([]);
  const [isAdding, setIsAdding] = useState(false);
  const [newAttr, setNewAttr] = useState(null);
  const [newVal, setNewVal] = useState([]);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [showTemplate, setShowTemplate] = useState(false);
  const [progress, setProgress] = useState(null);
  const [tip, setTip] = useState("Loading, please wait...");
  const [loading, setLoading] = useState(false);
  // const [docVersion, setDocVersion] = useState("");
  // const [jobId, setJobId] = useState("");
  const [indication, setIndication] = useState("");
  const [productName, setProductName] = useState("");
  const [synopsisText, setSynopsisText] = useState([]);
  // const [procedures, setProcedures] = useState([]);
  // const [statusCode, setStatusCode] = useState(null);
  const [euctNumber, setEUCTNumber] = useState(null);

  const { user } = useAuth();
  const userEmail = user.username || "";
  const userId = (user?.name.match(/\b\d{5}\b/) || [])[0] || "";
  // console.log(user);

  const pollIntervalRef = useRef(null);
  const abortControllerRef = useRef(null);

  const abortControllerHandle = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort(); // cancels the request
    }
  };

  const clearIntervalHandle = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }
  };

  // delete with confirmation
  const handleDelete = (file) => {
    Modal.confirm({
      title: "Delete File",
      content: `Are you sure you want to delete "${file.name}"?`,
      okText: "Yes",
      cancelText: "No",
      onOk: () => {
        abortControllerHandle();
        clearIntervalHandle();
        setFileList([]);
        setUploadSuccess(false);
        setMetadata([]);
      },
    });
  };

  // Poll digitization status
  const pollDigitizationProgress = (protocol_id) => {
    clearIntervalHandle();

    pollIntervalRef.current = setInterval(async () => {
      try {
        const res = await getPLPSDigitizationPercent({ protocol_id });
        // const percent = parseInt(res?.data?.digitization_percent, 10) || 0;
        const percent = parseInt(res?.message, 10) || 0;
        setProgress(percent);
        // setTip(res.message);
        setTip("Status fetched successfully...");

        if (percent >= 90) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
          message.success("Digitization complete!");
        }
      } catch (err) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
        if (err?.response?.status === 504) {
          message.error("Server timed out. Please try again.");
        } else {
          message.error("Failed to fetch digitization progress.");
        }
        setProgress(null);
        onComplete?.();
      }
    }, 6000);
  };

  // Upload handling with API
  const handleUpload = async () => {
    if (!fileList.length) {
      message.warning("Please select a file to upload.");
      return;
    }
    const file = fileList[0];
    const file_name = file.name;
    const base64File = await fileToBase64(file);

    const payload = {
      user_id: userId,
      file_b64: base64File,
      file_name: file.name,
    };
    // console.log("Payload Data", payload);

    try {
      setLoading(true);
      const response = await uploadPLPSFile(payload);
      // console.log("Upload API response", response);
      message.success(response.message);
      setTip(response.message);

      const { protocol_id, file_path } = response;
      // const { document_id } = response.data || {};

      setProtocolId(protocol_id);
      setProtocolName(file_name);
      setUploadSuccess(true);
      setTip("File is being digitized...");
      // create a fresh controller
      abortControllerRef.current = new AbortController();

      digitizePLPSFile(
        {
          file_name: file_path,
          file_id: protocol_id,
          user_id: userId,
        },
        abortControllerRef.current.signal
      )
        .then((res) => {
          // console.log("Digitization Response", res);

          // setStatusCode(res?.status_code);
          // setDocVersion(res?.data?.doc_version);
          // setJobId(res?.data?.job_id);

          const apiMetadata = res?.indication || {};

          setIndication(apiMetadata?.indication_name);
          setProductName(apiMetadata?.product_name);
          setSynopsisText(res?.synopsis_text);
          // setProcedures(apiMetadata?.procedures);
          setEUCTNumber(apiMetadata?.euct_number)
          // setProtocolId(apiMetadata?.protocol_number);
          setMetadata([
            // { key: "Protocol ID", value: protocol_id, deletable: false },
            { key: "Product Name", value: apiMetadata?.product_name || "N/A", deletable: false },
            { key: "Indication", value: apiMetadata?.indication_name || "N/A", deletable: false },
          ]);
          setLoading(false);
          setProgress(null);
        })
        .catch((err) => {
          console.log(err);
          if (err?.response?.status === 504) {
            message.error("Server timed out. Please try again.");
          } else {
            message.error("Digitization Failed");
          }
          setLoading(false);
          setProgress(null);
          setProtocolId(null);
          setProtocolName(null);

          // setStatusCode(err?.code);
          // stop polling if digitization fails
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
        });
      pollDigitizationProgress(protocol_id);
    } catch (error) {
      setLoading(false);
      console.log("inside outer Catch", error);
      message.error("Upload or digitization failed.");
    }
  };

  const handleDeleteMetadata = (index) => {
    setMetadata((prev) => prev.filter((_, i) => i !== index));
  };

  const draggerProps = {
    multiple: false,
    maxCount: 1,
    beforeUpload: (file) => {
      // Restrict file types (only PDF & DOCX) — accept attribute alone
      // doesn't reliably block drag-and-drop or all file pickers.
      const allowedTypes = [
        "application/pdf",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ];
      const allowedExtensions = [".pdf", ".docx"];

      const fileExt = file.name.slice(file.name.lastIndexOf(".")).toLowerCase();
      const isAllowedType = allowedTypes.includes(file.type) || allowedExtensions.includes(fileExt);

      if (!isAllowedType) {
        Modal.error({
          title: "Invalid File Type",
          content: "Only PDF and DOCX files are allowed.",
        });
        return Upload.LIST_IGNORE; // reject
      }

      setFileList([file]);
      setUploadSuccess(false);
      setMetadata([]);
      return false;
    },
    onRemove: () => {
      setFileList([]);
      setUploadSuccess(false);
      setMetadata([]);
    },
    fileList,
    accept:
      ".pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  };

  const handleBack = () => {
    const isFileListEmpty = fileList.length === 0;
    const modalTitle = isFileListEmpty ? "Are you sure ?" : "Discard Changes?";
    const modalContent = isFileListEmpty
      ? "Do you want to go back?"
      : "Your file will be discarded if you go back. Are you sure?";

    Modal.confirm({
      title: modalTitle,
      content: modalContent,
      okText: "Yes, Go Back",
      cancelText: "Stay",
      okType: "danger",
      onOk() {
        onBack?.();
        abortControllerHandle();
        clearIntervalHandle();
        setFileList([]);
        setUploadSuccess(false);
        setMetadata([]);
      },
    });
  };

  if (showTemplate) {
    return (
      <PLPSTemplate
        protocolId={protocolId}
        protocolName={protocolName}
        synopsisText={synopsisText}
        // jobId={jobId}
        // docVersion={docVersion}
        // procedures={procedures}
        userEmail={userEmail}
        userId={userId}
        onBack={() => setShowTemplate(false)}
        fileList={fileList}
        euctNumber={euctNumber}
      />
    );
  }

  const plpsContextValue = {
    fileList,
    setFileList,
    handleUpload,
    metadata,
    setMetadata,
    progress,
    protocolId,
    protocolName,
    uploadSuccess,
    setShowTemplate,
  };

  return (
    <PLPSContext.Provider value={plpsContextValue}>
      <div style={{ padding: "12px" }}>
        <Spin
          spinning={loading}
          tip={
            progress !== null ? (
              <div style={{ textAlign: "center" }}>
                <div style={{ marginBottom: 8 }}>{tip}</div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <div style={{ width: "70%" }}>
                    <Progress percent={progress} size="small" />
                  </div>
                </div>
              </div>
            ) : (
              tip
            )
          }
        >
          <GlobalStyle />

          {/* Top Bar */}
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              marginBottom: "8px",
              gap: "2rem",
            }}
          >
            <StyledButton onClick={handleBack} size="small">
              Back
            </StyledButton>
            {fileList?.[0]?.name && <FileNameTag fileName={fileList?.[0]?.name} />}
          </div>

          <Title level={5} style={{ color: "#426bba", lineHeight: 1.4 }}>
            <span style={{ fontSize: "17px", fontWeight: 600 }}>For 'PLPS' Deliverable,</span> <br />
            <span style={{ fontSize: "19px", fontWeight: 800 }}>Choose a Protocol:</span>
          </Title>

          <Dragger {...draggerProps} fileList={fileList} showUploadList={false}>
            <p className="ant-upload-drag-icon">
              <InboxOutlined style={{ color: "#426BBA" }} />
            </p>
            <p className="ant-upload-text">Click or Drag files here to upload</p>
            <p className="ant-upload-hint">Supports PDF/DOCX file</p>
          </Dragger>

          {fileList.length > 0 && (
            <div style={{ marginTop: "10px" }}>
              <h6 style={{ fontSize: 11, margin: "5px 0px" }}>Selected File:</h6>
              {fileList.map((file) => (
                <span key={file.uid} style={{ display: "flex", alignItems: "center", marginBottom: "5px" }}>
                  <span style={{ flex: 1 }}>{file.name}</span>
                  <DeleteOutlined style={{ color: "red", cursor: "pointer" }} onClick={() => handleDelete(file)} />
                </span>
              ))}
            </div>
          )}

          {uploadSuccess && (
            <>
              <div
                style={{
                  display: "flex",
                  justifyContent: "right",
                  alignItems: "center",
                  marginTop: "16px",
                  marginBottom: "8px",
                }}
              >
                {/* <Text
                  style={{
                    fontSize: "11px",
                    color: "#426bba",
                    cursor: "pointer",
                    fontWeight: "600",
                  }}
                  onClick={() => setIsAdding(true)}
                >
                  <PlusOutlined /> Add Attribute
                </Text> */}
              </div>

              <StyledTableWrapper>
                <Table
                  size="small"
                  pagination={false}
                  dataSource={[
                    ...metadata.map((m, i) => ({
                      key: i,
                      attribute: m.key,
                      value: m.value,
                      action: m.deletable ? (
                        <Button
                          icon={<DeleteOutlined />}
                          size="small"
                          type="text"
                          danger
                          onClick={() => handleDeleteMetadata(i)}
                        />
                      ) : null,
                    })),
                    isAdding && {
                      key: "new",
                      attribute: (
                        <Input
                          size="small"
                          placeholder="Attribute"
                          value={newAttr}
                          onChange={(e) => setNewAttr(e.target.value)}
                        />
                      ),
                      value: (
                        <Input
                          size="small"
                          placeholder="Value"
                          value={newVal}
                          onChange={(e) => setNewVal(e.target.value)}
                        />
                      ),
                      action: (
                        <Space>
                          <StyledButton
                            size="small"
                            onClick={() => {
                              if (newAttr && newVal) {
                                setMetadata((prev) => [...prev, { key: newAttr, value: newVal, deletable: true }]);
                                setNewAttr("");
                                setNewVal("");
                                setIsAdding(false);
                              }
                            }}
                          >
                            Add
                          </StyledButton>
                          <StyledButton
                            size="small"
                            onClick={() => {
                              setNewAttr("");
                              setNewVal("");
                              setIsAdding(false);
                            }}
                          >
                            Cancel
                          </StyledButton>
                        </Space>
                      ),
                    },
                  ].filter(Boolean)}
                  columns={[
                    { title: "Attribute", dataIndex: "attribute", key: "attribute" },
                    { title: "Value", dataIndex: "value", key: "value" },
                    ...(metadata.some((m) => m.deletable) || isAdding
                      ? [{ title: "Action", dataIndex: "action", key: "action" }]
                      : []),
                  ]}
                />
              </StyledTableWrapper>
            </>
          )}

          <div style={{ display: "flex", justifyContent: "center", marginTop: 20 }}>
            {uploadSuccess ? (
              <Tooltip
                // title={
                //   statusCode !== 200
                //     ? "Digitization failed. Please retry."
                //     : !indication || !productName || !procedures
                //       ? "Digitization failed. Missing Indication, Product Name, or Procedures."
                //       : ""
                // }
                title={
                  !indication || !productName
                    ? "Digitization failed. Missing Indication, Product Name."
                    : "Click to Load Template"
                }
              >
                <StyledButton
                  type="primary"
                  onClick={() => setShowTemplate(true)}
                  icon={<ArrowRightOutlined />}
                  // disabled={statusCode !== 200 || !indication || !productName || !procedures || isAdding}
                  disabled={!indication || !productName || isAdding}
                >
                  Next
                </StyledButton>
              </Tooltip>
            ) : (
              <StyledButton type="primary" onClick={handleUpload}>
                Upload Protocol
              </StyledButton>
            )}
          </div>
        </Spin>
      </div>
    </PLPSContext.Provider>
  );
};

export default PLPS;
